import React from "react";
import { View, Text } from "react-native";
// Lucide React - Using only confirmed available icons
import {
  createLucideIcon,
  // Core icons - definitely available
  Home,
  ListChecks,
  Cake,
  BellPlus,
  ShoppingCart,
  CalendarDays,
  QrCode,
  Users,
  Settings,
  Search,
  Plus,
  Minus,
  Heart,
  Star,
  StarHalf,
  ThumbsUp,
  Phone,
  Mail,
  Calendar,
  Camera,
  Image,
  Edit,
  Check,
  X,
  Lock,
  Eye,
  EyeOff,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowDown,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Menu,
  MoreVertical,
  MessageCircle,
  Bell,
  LogOut,
  LogIn,
  Info,
  HelpCircle,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  Filter,
  List,
  Bookmark,
  Folder,
  File,
  FileText,
  Copy,
  Save,
  Code,
  Smartphone,
  Wifi,
  Building,
  DollarSign,
  CreditCard,
  Calculator,
  Clock,
  MapPin,
  Shield,
  User,
  UserPlus,
  UserCircle,
  Share2,
  Flag,
  Bitcoin,

  // Advertisement Category Icons - confirmed available
  TrendingUp,
  Briefcase,
  BookOpen,
  Music,
  Tag,

  // Additional safe icons
  Sun,
  Moon,
  Cloud,
  Globe,
  Compass,
  Square,
  SquareCheck,
  Circle,
  Triangle,
  RefreshCw,
  ArrowUpDown,
  Video,
  Images,
  Battery,
  Map,

  // Safe alternatives
  Target,
  Crosshair,
  Layout,
  Layers,
  Hash,
  Type,
  Zap,
  ShoppingBag,
  Apple,
  Wrench,
  Wheat,
  Hammer,
  Crown,
  Badge,
  Gift,
  Lightbulb,
  Trash2,
  Mic,
  Maximize2,
  Minimize2,
} from "lucide-react";

// Product-specific outline icons share Lucide's sizing and stroke behaviour.
const TasksChecklist = createLucideIcon("TasksChecklist", [
  ["rect", {x: 3, y: 2, width: 5, height: 5, rx: 1, key: "box1"}],
  ["rect", {x: 3, y: 9.5, width: 5, height: 5, rx: 1, key: "box2"}],
  ["rect", {x: 3, y: 17, width: 5, height: 5, rx: 1, key: "box3"}],
  ["path", {d: "M12 4.5h9M12 12h9M12 19.5h9", key: "lines"}],
]);
const RibbonReminder = createLucideIcon("RibbonReminder", [
  ["path", {d: "M8 21v-2.3l-3.2-5a1.5 1.5 0 0 1 2.4-1.8L9 14V4.5a1.5 1.5 0 0 1 3 0V11l1-.5a1.5 1.5 0 0 1 2 .7 1.5 1.5 0 0 1 2.5.8 1.5 1.5 0 0 1 2 1.4v3.1c0 1.8-1.5 3.1-1.5 4.5", key: "hand"}],
  ["path", {d: "M9 8h3M10.5 8C3 2 2.5 11 10.5 8Zm0 0C18 2 18.5 11 10.5 8Zm0 0L5 11.5M10.5 8l6 3.5", key: "ribbon"}],
]);

// Comprehensive icon mapping for Outback Exchange app
const iconMap = {
  // Navigation & Core
  "tasks-checklist": TasksChecklist,
  "ribbon-reminder": RibbonReminder,
  home: Home,
  "list-checks": ListChecks,
  cake: Cake,
  "bell-plus": BellPlus,
  "shopping-cart": ShoppingCart,
  "calendar-days": CalendarDays,
  "qr-code": QrCode,
  store: ShoppingBag, // Using ShoppingBag as store alternative
  users: Users,
  settings: Settings,
  search: Search,
  plus: Plus,
  minus: Minus,
  "plus-circle": Plus, // Using Plus as plus-circle alternative
  magnify: Search,

  // Food and marketplace
  "food-apple": Apple, // Proper apple icon for food
  "account-group": Users,
  wrench: Wrench, // Proper wrench icon for tools/services
  "clock-outline": Clock,

  // Basic actions
  heart: Heart,
  star: Star,
  "star-outline": Star,
  "star-half": StarHalf,
  "star-filled": Star,
  "thumb-up-outline": ThumbsUp,
  "thumb-up-filled": ThumbsUp,
  "map-marker": MapPin,
  "map-pin": MapPin,
  phone: Phone,
  email: Mail,
  mail: Mail,
  calendar: Calendar,
  clock: Clock,
  camera: Camera,
  image: Image,
  edit: Edit,
  delete: X, // Using X as delete alternative
  trash: Trash2, // Trash icon for delete actions
  check: Check,
  close: X,
  account: User,
  "account-plus": UserPlus,
  lock: Lock,
  "lock-check": Lock,
  eye: Eye,
  "eye-off": EyeOff,

  // Navigation arrows
  "arrow-left": ArrowLeft,
  "arrow-right": ArrowRight,
  "arrow-up": ArrowUp,
  "arrow-down": ArrowDown,
  "chevron-up": ChevronUp,
  "chevron-down": ChevronDown,
  "chevron-left": ChevronLeft,
  "chevron-right": ChevronRight,
  menu: Menu,
  "more-vert": MoreVertical,

  // Communication
  comment: MessageCircle,
  "message-circle": MessageCircle,
  chat: MessageCircle,
  bell: Bell,
  notification: Bell,
  "help-circle-outline": HelpCircle,
  "help-circle": HelpCircle,
  shield: Shield,

  // User
  user: User,
  "user-circle": UserCircle,
  profile: User,
  "account-cog": Settings,
  logout: LogOut,
  login: LogIn,

  // Status
  info: Info,
  help: HelpCircle,
  warning: AlertTriangle,
  error: AlertCircle,
  "alert-circle": AlertCircle,
  success: CheckCircle,
  "check-circle": CheckCircle,

  // Actions
  refresh: RefreshCw,
  filter: Filter,
  sort: ArrowUpDown,
  "swap-horizontal": ArrowUpDown,
  grid: Layout, // Using Layout as grid alternative
  list: List,
  share: Share2,
  report: Flag,
  layers: Layers, // Split items icon
  maximize: Maximize2,
  minimize: Minimize2,

  // Content
  bookmark: Bookmark,
  folder: Folder,
  document: File,
  "file-text": FileText,
  clipboard: Copy,
  copy: Copy,
  tag: Tag,
  save: Save,
  code: Code,

  // Media
  video: Video,
  photo: Image,
  gallery: Images,

  // Devices
  smartphone: Smartphone,
  wifi: Wifi,
  battery: Battery,

  // Business
  building: Building,
  office: Building,
  money: DollarSign,
  "credit-card": CreditCard,
  bitcoin: Bitcoin,
  calculator: Calculator,

  // Nature (for rural theme)
  sun: Sun,
  moon: Moon,
  cloud: Cloud,
  tree: Type, // Using Type as tree alternative
  leaf: Type, // Using Type as leaf alternative
  farm: Home,
  water: Cloud,
  fire: Zap,
  fence: Hash,

  // Travel
  map: Map,
  globe: Globe,
  compass: Compass,
  tent: Home,
  mountain: Triangle, // Using Triangle as mountain alternative
  wheat: Wheat, // Agricultural/farming icon
  hammer: Hammer, // Construction/service icon
  crown: Crown, // Admin/elevated privileges icon
  badge: Badge, // Admin credentials/authority icon
  "user-cog": Settings, // User settings - admin management

  // Basic shapes
  square: Square,
  "square-check": SquareCheck,
  circle: Circle,
  triangle: Triangle,
  megaphone: Bell, // Using Bell as megaphone alternative
  x: X,
  "close-circle": X,
  "close-outline": X,
  "close-circle-outline": X,
  "chip-close": X,
  cancel: X,

  // Advertisement Category Icons
  "trending-up": TrendingUp,
  target: Target,
  crosshair: Crosshair,
  rocket: Target, // Using Target as rocket alternative
  tool: Hammer, // Using Hammer as tool alternative
  briefcase: Briefcase,
  "book-open": BookOpen,
  music: Music,

  // Style Guide & UI Icons
  "cursor-pointer": Target, // Using Target as cursor alternative
  palette: Layers, // Using Layers as palette alternative
  ruler: Type, // Using Type as ruler alternative
  card: Layout, // Using Layout as card alternative
  "format-list-bulleted": List,

  // Additional aliases for common patterns
  "store-outline": ShoppingBag,
  "home-outline": Home,
  "user-outline": User,
  "bell-outline": Bell,
  "heart-outline": Heart,

  // Bartering screen icons
  gift: Gift,
  lightbulb: Lightbulb,
  
  // Voice input
  mic: Mic,
  microphone: Mic,

  // Additional icons that might be used
  "shield-check": Shield, // Using Shield as shield-check alternative
  "account-search": Search, // Using Search as account-search alternative
  "account-wrench": Wrench, // Using Wrench as account-wrench alternative
  "cash-multiple": DollarSign, // Using DollarSign as cash-multiple alternative
  "chart-line": TrendingUp, // Using TrendingUp as chart-line alternative
  "currency-usd": DollarSign, // Using DollarSign as currency-usd alternative
};

const Icon = ({
  name,
  size = 24,
  color = "#000",
  style,
  className,
  bold = false,
  fill: fillProp,
  ...props
}) => {
  const IconComponent = iconMap[name];

  if (!IconComponent) {
    console.warn(
      `Icon "${name}" not found in iconMap. Available icons:`,
      Object.keys(iconMap).sort(),
    );
    // Return a simple 'x' as fallback instead of trying to render an icon
    return (
      <Text
        style={{
          fontSize: size * 0.5,
          color: "#ffffff",
          fontWeight: "bold",
          textAlign: "center",
          ...style,
        }}
      >
        x
      </Text>
    );
  }

  // Special handling for filled icons
  const isFilled =
    name === "star-filled" || 
    name === "thumb-up-filled" ||
    name === "shield-check" ||
    (name === "heart" && name !== "heart-outline");
  // Use fillProp if provided, otherwise use default logic
  const fill = fillProp !== undefined ? fillProp : (isFilled ? color : "none");

  return (
    <View
      style={{
        alignItems: "center",
        justifyContent: "center",
        width: size,
        height: size,
        ...style,
      }}
      {...props}
    >
      <IconComponent
        size={size}
        color={color}
        fill={fill}
        strokeWidth={bold ? 3 : 2}
      />
    </View>
  );
};

export default Icon;
